import { useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import { Bar, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { BILL_TYPES, TYPE_COLORS } from "@/lib/bills";

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short" });

/** 12-month stacked spend per bill type (+ kWh line for electricity) for the selected year. */
export function BillCostChart({ year }) {
  const valid = /^\d{4}$/.test(String(year));
  const { data } = useQuery({ queryKey: ["bills-yearly", year], queryFn: () => api.billsYearly(year), enabled: valid });
  if (!valid || !data) return null;
  const chart = data.chart.map((p) => ({ ...p, label: monthLabel(p.month) }));
  const hasKwh = chart.some((p) => p.kwh > 0);
  const usedTypes = BILL_TYPES.filter((t) => chart.some((p) => p[t.value] > 0));
  if (data.total === 0) {
    return (
      <div className="border border-dashed border-border rounded-md px-4 py-3 mb-4 text-xs text-muted-foreground" data-testid="bills-chart-empty">
        No bill payments recorded in {year} yet — the monthly cost chart appears once payments are marked paid.
      </div>
    );
  }
  return (
    <div className="border border-border rounded-md bg-white p-4 mb-4" data-testid="bills-chart">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <BarChart3 className="size-4 text-primary" />
        <span className="overline">Rent &amp; utilities spend {year}</span>
        <span className="ml-auto text-xs text-muted-foreground">Total <b className="mono text-foreground" data-testid="bills-chart-total">{fmtMoney(data.total)}</b> · avg <b className="mono text-foreground">{fmtMoney(data.total / 12)}</b>/month</span>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chart} margin={{ top: 4, right: hasKwh ? 8 : 0, left: 0, bottom: 0 }}>
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="money" tick={{ fontSize: 10 }} width={54} tickFormatter={(v) => fmtMoney(v)} />
          {hasKwh && <YAxis yAxisId="kwh" orientation="right" tick={{ fontSize: 10 }} width={44} tickFormatter={(v) => `${v}`} />}
          <Tooltip formatter={(v, name) => (name === "kWh" ? [`${v} kWh`, name] : [fmtMoney(v), name])} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {usedTypes.map((t) => <Bar key={t.value} yAxisId="money" dataKey={t.value} name={t.label} stackId="spend" fill={TYPE_COLORS[t.value]} radius={[2, 2, 0, 0]} />)}
          {hasKwh && <Line yAxisId="kwh" type="monotone" dataKey="kwh" name="kWh" stroke="#0A0A0A" strokeWidth={2} dot={{ r: 3 }} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
